// src/services/flow/components/AudioPlayer.jsx
import React, { useState, useRef, useEffect } from 'react';
import { PlayArrow, Pause } from '@mui/icons-material';
import { IconButton, Slider, Box, Typography } from '@mui/material';

const AudioPlayer = ({ audioUrl }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(new Audio(audioUrl));

  useEffect(() => {
    const audio = audioRef.current;
    
    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };
    
    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      audio.currentTime = 0;
    };
    
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);
    
    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
      audio.pause();
    };
  }, [audioUrl]);

  useEffect(() => {
    audioRef.current.src = audioUrl;
  }, [audioUrl]);

  const handlePlayPause = () => {
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSliderChange = (_, newValue) => {
    audioRef.current.currentTime = newValue;
    setCurrentTime(newValue);
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1, 
        backgroundColor: 'background.paper',
        borderRadius: 0,
        p: 1.7,
        pt: 0.9,
        pb: 0.9,
        pr: 1.5
      }}
    >
      <IconButton 
        size="small" 
        onClick={handlePlayPause}
        disabled={!audioUrl}
      >
        {isPlaying ? <Pause /> : <PlayArrow />}
      </IconButton>
      <Typography variant="caption" sx={{ minWidth: 40 }}>
        {formatTime(currentTime)}
      </Typography>
      <Slider
        size="small"
        value={currentTime}
        max={duration}
        onChange={handleSliderChange}
        disabled={!audioUrl}
        sx={{ 
          mx: 1,
          '& .MuiSlider-thumb': {
            width: 12,
            height: 12,
          }
        }}
      />
      <Typography variant="caption" sx={{ minWidth: 40 }}>
        {formatTime(duration)}
      </Typography>
    </Box>
  );
};

export default AudioPlayer;